// Canonical browser-side message envelope.
//
// Decode pipeline: ws frame → JsMsg → parseSubject → decode by Content-Type
// → JsEnvelope. Carries the three timestamps from the agent2 server design
// (device / bridge / server) so downstream code can reason about clock
// skew + replay semantics without re-parsing headers.
//
// `natsMsgId` is for tracing only — client dedups on
// (systemId, metric, deviceTimeMs); the broker's Duplicates: 10m window
// already covers publisher retries.

import type { JsMsg } from 'nats.ws';
import { decodeReading, decodeBatch, type DecodedReading } from './proto';
import { parseSubject, type ParsedSubject } from './subject';

export interface JsEnvelope {
  /** Raw subject, e.g. "acme-corp.dallas.lab-001.sensor.batch" */
  subject: string;
  /** Stream sequence — monotonic per stream, used for resume (OptStartSeq). */
  streamSeq: number;
  /** Broker wall-clock at receive (ms since epoch). Source of truth for skew. */
  serverTimeMs: number;
  /** Bridge wall-clock at publish (ms). null on legacy publishes without the
   *  X-Bridge-Time-Ms header. */
  bridgeTimeMs: number | null;
  /** Deterministic id stamped by the bridge — for tracing only. */
  natsMsgId: string;
  /** Parsed three-segment scope. */
  tenant: string;
  site: string;
  systemId: string;
  /** Free-form remainder of the subject — "sensor.batch", "logs.errwrn", etc. */
  eventTopic: string;
  payload: SensorBatchPayload | SensorReadingPayload | LogPayload | HeartbeatPayload | AlertPayload;
}

export interface SensorReadingPayload {
  kind: 'sensor';
  /** Canonical event time. Drives all client-side ordering, charting, dedup. */
  deviceTimeMs: number;
  nums: Record<string, number>;
  strs: Record<string, string>;
}
export interface SensorBatchPayload   { kind: 'batch';     readings: SensorReadingPayload[]; }
export interface LogPayload {
  kind: 'log';
  deviceTimeMs: number;
  level: 'debug' | 'info' | 'warn' | 'error' | 'fault';
  message: string;
  raw: Record<string, string>;
}
export interface HeartbeatPayload     { kind: 'heartbeat'; deviceTimeMs: number; raw: Record<string, unknown>; }
export interface AlertPayload         { kind: 'alert';     deviceTimeMs: number; event: Record<string, unknown>; }

export function fromJsMsg(msg: JsMsg): JsEnvelope | null {
  const parsed: ParsedSubject | null = parseSubject(msg.subject);
  if (!parsed) return null;

  const headers = msg.headers;
  const ct          = headers?.get('Content-Type') ?? '';
  const bridgeRaw   = headers?.get('X-Bridge-Time-Ms') ?? '';
  const natsMsgId   = headers?.get('Nats-Msg-Id') ?? '';
  const info        = msg.info;
  const streamSeq   = info?.streamSequence ?? msg.seq ?? 0;
  // timestampNanos comes through as a number; div to ms keeps precision well
  // under what JS numbers can represent for any plausible date.
  const serverTimeMs = info?.timestampNanos != null
    ? Math.floor(info.timestampNanos / 1_000_000)
    : Date.now();
  const bridgeTimeMs = bridgeRaw ? Number(bridgeRaw) : null;

  let payload: JsEnvelope['payload'] | null;
  try {
    payload = decodePayload(parsed.eventTopic, ct, msg.data, bridgeTimeMs ?? serverTimeMs);
  } catch {
    return null;
  }
  if (!payload) return null;

  return {
    subject: msg.subject,
    streamSeq, serverTimeMs, bridgeTimeMs, natsMsgId,
    tenant: parsed.tenant, site: parsed.site, systemId: parsed.systemId, eventTopic: parsed.eventTopic,
    payload,
  };
}

function decodePayload(
  eventTopic: string,
  ct: string,
  data: Uint8Array,
  fallbackTsMs: number,
): JsEnvelope['payload'] | null {
  if (eventTopic.startsWith('sensor.')) {
    if (eventTopic === 'sensor.batch' && ct.includes('x-protobuf-batch')) {
      const readings = decodeBatch(data).map((r) => toSensorReadingWithFallback(r, fallbackTsMs));
      if (readings.length === 0) return null;
      return { kind: 'batch', readings };
    }
    if (ct.includes('x-protobuf')) {
      return toSensorReadingWithFallback(decodeReading(data), fallbackTsMs);
    }
    // Legacy JSON sensor row. Two shapes in the wild:
    //   1. Flat:    {"Chi_Betriebsart":11,...,"Status":"Idle","Time":"05:01:12"}
    //      Older bridge, fields at top level, `Time` as HH:MM:SS string.
    //   2. Wrapped: {"nums":{...},"strs":{...},"ts_ms":1778230886040}
    //      Newer bridge that emits proto-shaped JSON instead of binary proto.
    return decodeJsonSensor(data, fallbackTsMs);
  }
  if (eventTopic.startsWith('logs.')) {
    return decodeLog(ct, data, fallbackTsMs);
  }
  if (eventTopic === 'alerts.fired') {
    const event = JSON.parse(new TextDecoder().decode(data)) as Record<string, unknown>;
    const inner = (event.event && typeof event.event === 'object')
      ? event.event as Record<string, unknown>
      : event;
    const ts = typeof inner.firedAt === 'number' ? inner.firedAt as number
             : typeof inner.ts === 'number' ? inner.ts as number
             : fallbackTsMs;
    return { kind: 'alert', deviceTimeMs: ts, event: inner };
  }
  if (eventTopic === 'heartbeat.tick') {
    let raw: Record<string, unknown> = {};
    try { raw = JSON.parse(new TextDecoder().decode(data)) as Record<string, unknown>; } catch { /* */ }
    const ts = typeof raw.ts === 'number' ? raw.ts as number : fallbackTsMs;
    return { kind: 'heartbeat', deviceTimeMs: ts, raw };
  }
  return null;
}

function toSensorReadingWithFallback(r: DecodedReading, fallbackTsMs: number): SensorReadingPayload {
  // Fall back to the broker-stamped time when the bridge sent a payload
  // with no parseable device timestamp. Better to keep the row at +/- a
  // few hundred ms than to drop it entirely. Bridge wall-clock skew is
  // detectable separately via the X-Bridge-Time-Ms header.
  return {
    kind: 'sensor',
    deviceTimeMs: r.ts_ms > 0 ? r.ts_ms : fallbackTsMs,
    nums: r.nums,
    strs: r.strs,
  };
}

function decodeJsonSensor(data: Uint8Array, fallbackTsMs: number): SensorReadingPayload | null {
  const raw: unknown = JSON.parse(new TextDecoder().decode(data));
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;

  const wrappedNums =
    obj.nums && typeof obj.nums === 'object' && !Array.isArray(obj.nums)
      ? obj.nums as Record<string, unknown>
      : null;
  const wrappedStrs =
    obj.strs && typeof obj.strs === 'object' && !Array.isArray(obj.strs)
      ? obj.strs as Record<string, unknown>
      : null;

  // Numeric ts wins; string `Time` (HH:MM:SS) is unparseable without a
  // device timezone, so we fall back to the broker timestamp.
  let ts =
    typeof obj.ts === 'number' ? obj.ts as number :
    typeof obj.ts_ms === 'number' ? obj.ts_ms as number :
    typeof obj.Time === 'number' ? obj.Time as number :
    0;
  if (ts <= 0) ts = fallbackTsMs;

  const nums: Record<string, number> = {};
  const strs: Record<string, string> = {};

  if (wrappedNums || wrappedStrs) {
    if (wrappedNums) for (const [k, v] of Object.entries(wrappedNums)) {
      if (typeof v === 'number') nums[k] = v;
    }
    if (wrappedStrs) for (const [k, v] of Object.entries(wrappedStrs)) {
      if (typeof v === 'string') strs[k] = v;
    }
  } else {
    // Flat legacy shape — every top-level scalar is a field. `ts`/`ts_ms`/
    // `Time` are reserved; everything else is data.
    for (const [k, v] of Object.entries(obj)) {
      if (k === 'ts' || k === 'ts_ms' || k === 'Time') continue;
      if (typeof v === 'number') nums[k] = v;
      else if (typeof v === 'string') strs[k] = v;
    }
  }

  // Refuse to manufacture an entirely empty reading — that's a sign the
  // payload didn't match either shape. Lets the caller skip rendering an
  // empty row.
  if (Object.keys(nums).length === 0 && Object.keys(strs).length === 0) return null;

  return { kind: 'sensor', deviceTimeMs: ts, nums, strs };
}

function decodeLog(ct: string, data: Uint8Array, fallbackTsMs: number): LogPayload | null {
  // Two bridge formats are in the wild here:
  //   - format=proto: SensorReading-shaped proto with strs.level/source/message
  //     (bridge/src/main.rs json_to_reading -> emit_proto)
  //   - format=json:  raw clq-errwrn JSON, e.g.
  //     {"level":"warn","source":"Laser","message":"…","time":"06:23:24","raw":"…"}
  //     No numeric ts field — only a lowercase `time` HH:MM:SS string.
  // Pre-fix this branch returned null for the JSON shape because it only
  // checked obj.ts (number). Now we fall back to the broker-stamped
  // serverTimeMs the same way decodeJsonSensor does.
  let strs: Record<string, string> = {};
  let ts = 0;
  if (ct.includes('x-protobuf')) {
    const r = decodeReading(data);
    strs = r.strs;
    ts = r.ts_ms > 0 ? r.ts_ms : fallbackTsMs;
  } else {
    try {
      const obj = JSON.parse(new TextDecoder().decode(data)) as Record<string, unknown>;
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'string') strs[k] = v;
      }
      ts = typeof obj.ts === 'number' ? obj.ts as number :
           typeof obj.ts_ms === 'number' ? obj.ts_ms as number :
           fallbackTsMs;
    } catch {
      return null;
    }
  }
  // Refuse genuinely-empty payloads, but otherwise let the row through
  // even when the device clock didn't supply a usable ts.
  if (Object.keys(strs).length === 0) return null;
  const lvlRaw = (strs.level || 'info').toLowerCase();
  const level: LogPayload['level'] =
    lvlRaw === 'error' || lvlRaw === 'fault' ? 'error' :
    lvlRaw === 'warn'  || lvlRaw === 'warning' ? 'warn'  :
    lvlRaw === 'debug' ? 'debug' :
    'info';
  return {
    kind: 'log',
    deviceTimeMs: ts,
    level,
    message: strs.message || strs.raw || '',
    raw: strs,
  };
}
